import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ToolPanel from '../components/ToolPanel';
import TipsWindow from '../components/TipsWindow';
import { useTip } from '../hooks/useTips';
import {
  SLIDER_STYLES,
  FORM_STYLES,
  TYPOGRAPHY_STYLES,
  SECTION_STYLES,
  ROW_STYLES,
} from '../styles/viewerStyles';
import { getColormapGradient } from '../utils/colormapUtils';
import { useViewerSettingsContext } from '../context/ViewerSettingsContext';

const COLORMAP_KEYS = ['ColormapTurbo', 'ColormapViridis', 'ColormapMagma', 'ColormapInferno', 'ColormapCividis', 'ColormapGrayscale'];

const CLASSIFICATION_FILTER_CLASSES = [
  { value: 1, label: 'Unclassified', color: 'gray' },
  { value: 2, label: 'Ground', color: 'brown' },
  { value: 3, label: 'Low Vegetation', color: 'greenyellow' },
  { value: 4, label: 'Medium Vegetation', color: 'green' },
  { value: 5, label: 'High Vegetation', color: 'darkgreen' },
  { value: 6, label: 'Building', color: 'red' },
  { value: 7, label: 'Low Point', color: 'orange' },
];
const ALL_CLASS_VALUES = CLASSIFICATION_FILTER_CLASSES.map(c => c.value);

const FILTER_MENU_PROPS = {
  PaperProps: {
    sx: {
      ...FORM_STYLES.selectMenuProps.PaperProps.sx,
      maxHeight: 48 * 4.5 + 8,
      width: 220,
    },
  },
};

function SettingsTools({
  toggleSettingsPanel,
  DRAWER_WIDTH,
  hasGaussianOnly = false
}) {
  const {
    pointSize, setPointSize,
    useBrowserRecommendationResolution, setUseBrowserRecommendationResolution,
    eyeDomeLighting, setEyeDomeLighting,
    eyeDomeLightingStrength, setEyeDomeLightingStrength,
    eyeDomeLightingRadius, setEyeDomeLightingRadius,
    globeLighting, setGlobeLighting,
    atmosphereRendering, setAtmosphereRendering,
    terrainRendering, setTerrainRendering,
    contourLinesEnabled, setContourLinesEnabled,
    contourSpacing, setContourSpacing,
    thickLineInterval, setThickLineInterval,
    lineThickness, setLineThickness,
    fieldOfView, setFieldOfView,
    zoomFactor, setZoomFactor,
    selectedProperty, updateSelectedProperty,
    hasIntensity = true,
    classificationFilter = ALL_CLASS_VALUES,
    updateClassificationFilter,
    intensityRange, updateIntensityRange,
    elevationRange, updateElevationRange,
    distanceRange, updateDistanceRange,
    intensityColormap, updateIntensityColormap,
    elevationColormap, updateElevationColormap,
    distanceColormap, updateDistanceColormap,
    initialElevationMin, initialElevationMax,
    distanceBounds,
    visualFidelity, setVisualFidelity,
    ssaoEnabled, setSSAOEnabled,
    ssaoIntensity, setSSAOIntensity,
    fxaaEnabled, setFXAAEnabled,
  } = useViewerSettingsContext();
  const { t } = useTranslation();
  const COLORMAP_OPTIONS = COLORMAP_KEYS.map((key, i) => ({ value: i, label: t(key) }));

  const [pointCloudExpanded, setPointCloudExpanded] = useState(false);
  const [worldExpanded, setWorldExpanded] = useState(false);
  const [terrainExpanded, setTerrainExpanded] = useState(false);
  const [cameraExpanded, setCameraExpanded] = useState(false);
  const [renderingExpanded, setRenderingExpanded] = useState(false);

  // Tips integration - component is only mounted when panel is open
  const { isOpen: isTipOpen, title: tipTitle, markdownBody: tipBody, close: closeTip } = useTip('settings', true);

  return (
    <>
      <TipsWindow
        isOpen={isTipOpen}
        title={tipTitle}
        markdownBody={tipBody}
        onClose={({ doNotShowAgain }) => closeTip(doNotShowAgain)}
      />
      <ToolPanel
        title={t('SettingsCesium')}
        onClose={toggleSettingsPanel}
        width={DRAWER_WIDTH}
        maxHeight="calc(100vh - 160px)"
      >
        {/* Point Cloud Rendering Controls */}
        {!hasGaussianOnly && (
          <>
            <Box sx={SECTION_STYLES.expandableHeader}
              onClick={() => setPointCloudExpanded(!pointCloudExpanded)}>
              <Typography variant="subtitle2" sx={{ ...TYPOGRAPHY_STYLES.subtitle, flex: 1 }}>
                {t('PointCloudSettings')}
              </Typography>
              <IconButton sx={SECTION_STYLES.expandIcon(pointCloudExpanded)}>
                <ExpandMoreIcon />
              </IconButton>
            </Box>

            {pointCloudExpanded && (
              <>
                {/* Point Size Slider */}
                <Box sx={ROW_STYLES.sliderRow}>
                  <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                    {t('PointSize')}: {pointSize}
                  </Typography>
                  <Slider
                    value={pointSize}
                    min={1}
                    max={10}
                    step={1}
                    onChange={(_, value) => setPointSize(value)}
                    aria-labelledby="point-size-slider"
                    sx={SLIDER_STYLES.base}
                  />
                </Box>

                {/* Property Selection Dropdown */}
                <Box sx={ROW_STYLES.sliderRow}>
                  <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                    {t('Property')}:
                  </Typography>
                  <FormControl sx={{ flex: 1 }}>
                    <Select
                      value={selectedProperty}
                      onChange={(e) => updateSelectedProperty(e.target.value)}
                      size="small"
                      sx={FORM_STYLES.select}
                      MenuProps={FORM_STYLES.selectMenuProps}
                    >
                      <MenuItem value="color"><Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('Color')}</Typography></MenuItem>
                      {hasIntensity && <MenuItem value="intensity"><Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('Intensity')}</Typography></MenuItem>}
                      <MenuItem value="elevation"><Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('Elevation')}</Typography></MenuItem>
                      <MenuItem value="distance"><Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('Distance')}</Typography></MenuItem>
                      <MenuItem value="classification"><Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('Classification')}</Typography></MenuItem>
                      <MenuItem value="returnNumbers"><Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('Return Numbers')}</Typography></MenuItem>
                      <MenuItem value="numberOfReturns"><Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('Number of Returns')}</Typography></MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Classification Filter */}
                {selectedProperty === 'classification' && (
                  <Box sx={{ ...ROW_STYLES.sliderRow, mt: 1 }}>
                    <Typography variant="caption" sx={{ ...TYPOGRAPHY_STYLES.captionWithLabel, pr: 1 }}>
                      {t('ClassificationFilter')}:
                    </Typography>
                    <FormControl sx={{ flex: 1 }}>
                      <Select
                        multiple
                        value={classificationFilter}
                        onChange={(e) => {
                          const { value } = e.target;
                          updateClassificationFilter(typeof value === 'string' ? value.split(',').map(Number) : value);
                        }}
                        renderValue={(selected) =>
                          selected.length === ALL_CLASS_VALUES.length
                            ? t('AllClasses')
                            : CLASSIFICATION_FILTER_CLASSES
                              .filter(c => selected.includes(c.value))
                              .map(c => t(c.label))
                              .join(', ')
                        }
                        size="small"
                        sx={FORM_STYLES.select}
                        MenuProps={FILTER_MENU_PROPS}
                      >
                        {CLASSIFICATION_FILTER_CLASSES.map((cls) => {
                          const checked = classificationFilter.includes(cls.value);
                          const Icon = checked ? CheckBoxIcon : CheckBoxOutlineBlankIcon;
                          return (
                            <MenuItem key={cls.value} value={cls.value} dense>
                              <Icon fontSize="small" sx={{ mr: 1, color: cls.color }} />
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '2px',
                                  bgcolor: cls.color,
                                  mr: 0.75,
                                  border: '1px solid rgba(255,255,255,0.3)',
                                  flexShrink: 0,
                                }}
                              />
                              <ListItemText
                                primary={t(cls.label)}
                                primaryTypographyProps={{ variant: 'caption', fontSize: '0.75rem' }}
                              />
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Intensity Range Slider */}
                {selectedProperty === 'intensity' && (
                  <Box sx={{ ...ROW_STYLES.sliderRow, mt: 1 }}>
                    <Typography variant="caption" sx={{ ...TYPOGRAPHY_STYLES.captionWithLabel, pr: 1 }}>
                      {t('IntensityRange')}:
                    </Typography>
                    <Slider
                      value={intensityRange}
                      min={0}
                      max={255}
                      step={1}
                      onChange={(_, value) => updateIntensityRange(value)}
                      valueLabelDisplay="auto"
                      aria-labelledby="intensity-range-slider"
                      sx={SLIDER_STYLES.withLabel}
                    />
                  </Box>
                )}
                {selectedProperty === 'intensity' && (
                  <Box sx={{ ...ROW_STYLES.sliderRow, mt: 1 }}>
                    <Typography variant="caption" sx={{ ...TYPOGRAPHY_STYLES.captionWithLabel, pr: 1 }}>
                      {t('ColorScale')}:
                    </Typography>
                    <FormControl sx={{ flex: 1 }}>
                      <Select value={intensityColormap} onChange={(e) => updateIntensityColormap(e.target.value)}
                        size="small" sx={FORM_STYLES.select} MenuProps={FORM_STYLES.selectMenuProps}
                        renderValue={(val) => {
                          const opt = COLORMAP_OPTIONS.find(o => o.value === val);
                          if (!opt) return null;
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</Typography>
                              <Box sx={{ width: 52, flexShrink: 0, height: 10, borderRadius: '3px', background: getColormapGradient(opt.value) }} />
                            </Box>
                          );
                        }}>
                        {COLORMAP_OPTIONS.map(o => (
                          <MenuItem key={o.value} value={o.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</Typography>
                              <Box sx={{ width: 52, flexShrink: 0, height: 10, borderRadius: '3px', background: getColormapGradient(o.value) }} />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Elevation Range Slider */}
                {selectedProperty === 'elevation' && (
                  <Box sx={{ ...ROW_STYLES.sliderRow, mt: 1 }}>
                    <Typography variant="caption" sx={{ ...TYPOGRAPHY_STYLES.captionWithLabel, pr: 1 }}>
                      {t('ElevationRange')}:
                    </Typography>
                    <Slider
                      value={elevationRange}
                      min={initialElevationMin}
                      max={initialElevationMax}
                      step={0.1}
                      onChange={(_, value) => updateElevationRange(value)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => value.toFixed(1)}
                      aria-labelledby="elevation-range-slider"
                      sx={SLIDER_STYLES.withLabel}
                    />
                  </Box>
                )}
                {selectedProperty === 'elevation' && (
                  <Box sx={{ ...ROW_STYLES.sliderRow, mt: 1 }}>
                    <Typography variant="caption" sx={{ ...TYPOGRAPHY_STYLES.captionWithLabel, pr: 1 }}>
                      {t('ColorScale')}:
                    </Typography>
                    <FormControl sx={{ flex: 1 }}>
                      <Select value={elevationColormap} onChange={(e) => updateElevationColormap(e.target.value)}
                        size="small" sx={FORM_STYLES.select} MenuProps={FORM_STYLES.selectMenuProps}
                        renderValue={(val) => {
                          const opt = COLORMAP_OPTIONS.find(o => o.value === val);
                          if (!opt) return null;
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</Typography>
                              <Box sx={{ width: 52, flexShrink: 0, height: 10, borderRadius: '3px', background: getColormapGradient(opt.value) }} />
                            </Box>
                          );
                        }}>
                        {COLORMAP_OPTIONS.map(o => (
                          <MenuItem key={o.value} value={o.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</Typography>
                              <Box sx={{ width: 52, flexShrink: 0, height: 10, borderRadius: '3px', background: getColormapGradient(o.value) }} />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Distance Range Slider */}
                {selectedProperty === 'distance' && (
                  <Box sx={{ ...ROW_STYLES.sliderRow, mt: 1 }}>
                    <Typography variant="caption" sx={{ ...TYPOGRAPHY_STYLES.captionWithLabel, pr: 1 }}>
                      {t('Distance Range')}:
                    </Typography>
                    <Slider
                      value={distanceRange}
                      min={distanceBounds?.[0] ?? 0}
                      max={distanceBounds?.[1] ?? Math.max(distanceRange[1], 1)}
                      step={1}
                      onChange={(_, value) => updateDistanceRange(value)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${Math.round(value)} m`}
                      aria-labelledby="distance-range-slider"
                      sx={SLIDER_STYLES.withLabel}
                    />
                  </Box>
                )}
                {selectedProperty === 'distance' && (
                  <Box sx={{ ...ROW_STYLES.sliderRow, mt: 1 }}>
                    <Typography variant="caption" sx={{ ...TYPOGRAPHY_STYLES.captionWithLabel, pr: 1 }}>
                      {t('ColorScale')}:
                    </Typography>
                    <FormControl sx={{ flex: 1 }}>
                      <Select value={distanceColormap} onChange={(e) => updateDistanceColormap(e.target.value)}
                        size="small" sx={FORM_STYLES.select} MenuProps={FORM_STYLES.selectMenuProps}
                        renderValue={(val) => {
                          const opt = COLORMAP_OPTIONS.find(o => o.value === val);
                          if (!opt) return null;
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</Typography>
                              <Box sx={{ width: 52, flexShrink: 0, height: 10, borderRadius: '3px', background: getColormapGradient(opt.value, true) }} />
                            </Box>
                          );
                        }}>
                        {COLORMAP_OPTIONS.map(o => (
                          <MenuItem key={o.value} value={o.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</Typography>
                              <Box sx={{ width: 52, flexShrink: 0, height: 10, borderRadius: '3px', background: getColormapGradient(o.value, true) }} />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </>
            )}
          </>
        )}

        {/* World Settings */}
        <Box sx={SECTION_STYLES.expandableHeader}
          onClick={() => setWorldExpanded(!worldExpanded)}>
          <Typography variant="subtitle2" sx={{ ...TYPOGRAPHY_STYLES.subtitle, flex: 1 }}>
            {t('WorldSettings')}
          </Typography>
          <IconButton sx={SECTION_STYLES.expandIcon(worldExpanded)}>
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        {worldExpanded && (
          <>
            {/* Globe Lighting Toggle */}
            <Box sx={ROW_STYLES.checkboxRow}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('GlobeLighting')}
              </Typography>
              <Checkbox
                checked={globeLighting}
                onChange={(e) => setGlobeLighting(e.target.checked)}
                sx={FORM_STYLES.checkbox}
              />
            </Box>

            {/* Atmosphere Rendering Toggle */}
            <Box sx={{ ...ROW_STYLES.checkboxRow, mb: 1 }}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('Atmosphere')}
              </Typography>
              <Checkbox
                checked={atmosphereRendering}
                onChange={(e) => setAtmosphereRendering(e.target.checked)}
                sx={FORM_STYLES.checkbox}
              />
            </Box>
          </>
        )}

        {/* Terrain Settings */}
        <Box sx={SECTION_STYLES.expandableHeader}
          onClick={() => setTerrainExpanded(!terrainExpanded)}>
          <Typography variant="subtitle2" sx={{ ...TYPOGRAPHY_STYLES.subtitle, flex: 1 }}>
            {t('TerrainSettings')}
          </Typography>
          <IconButton sx={SECTION_STYLES.expandIcon(terrainExpanded)}>
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        {terrainExpanded && (
          <>
            {/* Terrain Rendering Toggle */}
            <Box sx={ROW_STYLES.checkboxRow}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('Terrain')}
              </Typography>
              <Checkbox
                checked={terrainRendering}
                onChange={(e) => setTerrainRendering(e.target.checked)}
                sx={FORM_STYLES.checkbox}
              />
            </Box>

            {/* Contour Lines Toggle */}
            <Box sx={ROW_STYLES.checkboxRow}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('ContourLines')}
              </Typography>
              <Checkbox
                checked={contourLinesEnabled}
                onChange={(e) => setContourLinesEnabled(e.target.checked)}
                sx={FORM_STYLES.checkbox}
              />
            </Box>

            {/* Contour Spacing Slider */}
            <Box sx={ROW_STYLES.sliderRow}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('ContourSpacing')}: {contourSpacing}m
              </Typography>
              <Slider
                value={contourSpacing}
                min={1}
                max={10}
                step={0.5}
                onChange={(_, value) => setContourSpacing(value)}
                disabled={!contourLinesEnabled}
                aria-labelledby="contour-spacing-slider"
                sx={SLIDER_STYLES.disableable}
              />
            </Box>

            {/* Thick Line Interval Slider */}
            <Box sx={ROW_STYLES.sliderRow}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('ThickLineInterval')}: {thickLineInterval}
              </Typography>
              <Slider
                value={thickLineInterval}
                min={2}
                max={10}
                step={1}
                onChange={(_, value) => setThickLineInterval(value)}
                disabled={!contourLinesEnabled}
                aria-labelledby="thick-line-interval-slider"
                sx={SLIDER_STYLES.disableable}
              />
            </Box>

            {/* Line Thickness Slider */}
            <Box sx={{ ...ROW_STYLES.sliderRow, mb: 1.5 }}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('LineThickness')}: {lineThickness.toFixed(2)}
              </Typography>
              <Slider
                value={lineThickness}
                min={0.01}
                max={0.5}
                step={0.01}
                onChange={(_, value) => setLineThickness(value)}
                disabled={!contourLinesEnabled}
                aria-labelledby="line-thickness-slider"
                sx={SLIDER_STYLES.disableable}
              />
            </Box>
          </>
        )}

        {/* Camera Settings */}
        <Box sx={SECTION_STYLES.expandableHeader}
          onClick={() => setCameraExpanded(!cameraExpanded)}>
          <Typography variant="subtitle2" sx={{ ...TYPOGRAPHY_STYLES.subtitle, flex: 1 }}>
            {t('Camera')}
          </Typography>
          <IconButton sx={SECTION_STYLES.expandIcon(cameraExpanded)}>
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        {cameraExpanded && (
          <>
            {/* Field of View Slider */}
            <Box sx={ROW_STYLES.sliderRow}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('FieldOfView')}: {fieldOfView}°
              </Typography>
              <Slider
                value={fieldOfView}
                min={15}
                max={120}
                step={1}
                onChange={(_, value) => setFieldOfView(value)}
                aria-labelledby="field-of-view-slider"
                sx={SLIDER_STYLES.base}
              />
            </Box>

            {/* Zoom Speed Slider */}
            <Box sx={{ ...ROW_STYLES.sliderRow, mb: 1.5 }}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('ZoomSpeed') || 'Zoom Speed'}: {zoomFactor.toFixed(1)}
              </Typography>
              <Slider
                value={zoomFactor}
                min={0.1}
                max={10.0}
                step={0.1}
                onChange={(_, value) => setZoomFactor(value)}
                aria-labelledby="zoom-factor-slider"
                sx={SLIDER_STYLES.base}
              />
            </Box>
          </>
        )}

        {/* Rendering Settings */}
        <Box sx={SECTION_STYLES.expandableHeader}
          onClick={() => setRenderingExpanded(!renderingExpanded)}>
          <Typography variant="subtitle2" sx={{ ...TYPOGRAPHY_STYLES.subtitle, flex: 1 }}>
            {t('Rendering')}
          </Typography>
          <IconButton sx={SECTION_STYLES.expandIcon(renderingExpanded)}>
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        {renderingExpanded && (
          <>
            <Box sx={ROW_STYLES.sliderRow}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('VisualFidelity')}
              </Typography>
              <FormControl sx={{ flex: 1 }}>
                <Select
                  value={visualFidelity}
                  onChange={(e) => setVisualFidelity(e.target.value)}
                  size="small"
                  sx={FORM_STYLES.select}
                  MenuProps={FORM_STYLES.selectMenuProps}
                >
                  <MenuItem value={0}><Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('HighFidelity')}</Typography></MenuItem>
                  <MenuItem value={1}><Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('Balanced')}</Typography></MenuItem>
                  <MenuItem value={2}><Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{t('HighPerformance')}</Typography></MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={ROW_STYLES.checkboxRow}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('ReducePixelDensity')}
              </Typography>
              <Checkbox
                checked={useBrowserRecommendationResolution}
                onChange={(e) => setUseBrowserRecommendationResolution(e.target.checked)}
                sx={FORM_STYLES.checkbox}
              />
            </Box>

            {/* Eye Dome Lighting Toggle */}
            <Box sx={ROW_STYLES.checkboxRow}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('EyeDomeLighting')}
              </Typography>
              <Checkbox
                checked={eyeDomeLighting}
                onChange={(e) => setEyeDomeLighting(e.target.checked)}
                sx={FORM_STYLES.checkbox}
              />
            </Box>

            {/* Eye Dome Lighting Strength Slider */}
            <Box sx={ROW_STYLES.sliderRow}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('EDLStrength')}: {eyeDomeLightingStrength}
              </Typography>
              <Slider
                value={eyeDomeLightingStrength}
                min={0}
                max={5}
                step={0.1}
                onChange={(_, value) => setEyeDomeLightingStrength(value)}
                aria-labelledby="edl-strength-slider"
                sx={SLIDER_STYLES.base}
              />
            </Box>

            {/* Eye Dome Lighting Radius Slider */}
            <Box sx={{ ...ROW_STYLES.sliderRow, mb: 1.5 }}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('EDLRadius')}: {eyeDomeLightingRadius.toFixed(1)}
              </Typography>
              <Slider
                value={eyeDomeLightingRadius}
                min={0}
                max={2}
                step={0.1}
                onChange={(_, value) => setEyeDomeLightingRadius(value)}
                aria-labelledby="edl-radius-slider"
                sx={SLIDER_STYLES.base}
              />
            </Box>

            {/* Ambient Occlusion (SSAO) Toggle */}
            <Box sx={ROW_STYLES.checkboxRow}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('AmbientOcclusion')}
              </Typography>
              <Checkbox
                checked={ssaoEnabled}
                onChange={(e) => setSSAOEnabled(e.target.checked)}
                sx={FORM_STYLES.checkbox}
              />
            </Box>

            {/* SSAO Intensity Slider */}
            <Box sx={{ ...ROW_STYLES.sliderRow, mb: 1.5 }}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('AmbientOcclusionIntensity')}: {ssaoIntensity.toFixed(1)}
              </Typography>
              <Slider
                value={ssaoIntensity}
                min={0.1}
                max={10.0}
                step={0.1}
                onChange={(_, value) => setSSAOIntensity(value)}
                disabled={!ssaoEnabled}
                aria-labelledby="ssao-intensity-slider"
                sx={SLIDER_STYLES.disableable}
              />
            </Box>

            {/* FXAA Toggle */}
            <Box sx={{ ...ROW_STYLES.checkboxRow, mb: 1 }}>
              <Typography variant="caption" sx={TYPOGRAPHY_STYLES.captionWithLabel}>
                {t('FXAA')}
              </Typography>
              <Checkbox
                checked={fxaaEnabled}
                onChange={(e) => setFXAAEnabled(e.target.checked)}
                sx={FORM_STYLES.checkbox}
              />
            </Box>

          </>
        )}
      </ToolPanel>
    </>
  );
}

export default SettingsTools;
